import { File, Image as ImageIcon, Link2, Music4, Package, Play } from 'lucide-react'
import answerModel from 'components/answers/answerModel'
import { extractUrls } from 'components/shared/LinkPreview'
import { extractOrdinalOutpointFromUrl } from 'components/shared/ordinalUrlUtils'
import { getCachedAnswer } from 'state/cache'
import type { Answer, AnswerFileUrl } from 'types/answers'

// A post's small media, for a breadcrumb crumb: the preview image or the
// kind's icon (shared by the thread crumb row and the header breadcrumb).
export type ParentPayload =
    | { kind: 'image'; previewUrl: string }
    | { kind: 'video'; previewUrl?: string | null; icon: typeof Play }
    | { kind: 'audio'; icon: typeof Music4 }
    | { kind: 'ordinal'; previewUrl?: string | null; icon: typeof Package }
    | { kind: 'url'; previewUrl?: string | null; icon: typeof Link2 }
    | { kind: 'file'; icon: typeof File }
    | { kind: 'other'; icon: typeof ImageIcon }

const AUDIO_FILE_EXTENSION_RE = /\.(mp3|wav|ogg|oga|opus|m4a|aac|flac|webm|aif|aiff|aifc|caf|amr)$/i
const IMAGE_FILE_EXTENSION_RE = /\.(avif|bmp|gif|heic|heif|jpeg|jpg|png|svg|webp)$/i
const VIDEO_FILE_EXTENSION_RE = /\.(mp4|mov|m4v|webm|ogv|mkv|avi)$/i
const PREVIEW_VARIANT_KEYS = ['medium', 'small', '1x1', 'original'] as const

export const normalizeApiUrl = (rawUrl?: string | null) => {
    if (typeof rawUrl !== 'string' || !rawUrl) return rawUrl || null
    if (
        rawUrl.startsWith('//') ||
        rawUrl.startsWith('http://') ||
        rawUrl.startsWith('https://') ||
        rawUrl.startsWith('blob:') ||
        rawUrl.startsWith('data:')
    ) {
        return rawUrl
    }

    if (rawUrl.startsWith('/api/') && window.knovApiUrl) {
        const base = String(window.knovApiUrl).replace(/\/+$/, '')
        return `${base}${rawUrl}`
    }

    return rawUrl
}

const getPreferredPreviewUrl = (
    previewUrls?: Record<string, string> | null,
    fallbackUrl?: string | null,
) => {
    if (previewUrls) {
        for (const key of PREVIEW_VARIANT_KEYS) {
            if (previewUrls[key]) return previewUrls[key]
        }

        const firstPreview = Object.values(previewUrls)[0]
        if (firstPreview) return firstPreview
    }

    return fallbackUrl || null
}

const isOrdinalUrl = (address?: string | null) => {
    if (!address) return false
    return !!extractOrdinalOutpointFromUrl(address)
}

const isAudioFile = (file?: AnswerFileUrl | null) => {
    if (!file) return false

    const audioSourceType = (file.content_type || '').split(';')[0]?.trim().toLowerCase()
    const fileName = file?.name || ''

    return audioSourceType?.startsWith('audio/') || AUDIO_FILE_EXTENSION_RE.test(fileName)
}

const isImageFile = (file?: AnswerFileUrl | null) => {
    if (!file) return false

    const sourceType = (file.content_type || '').split(';')[0]?.trim().toLowerCase()
    const fileName = file?.name || ''

    return sourceType?.startsWith('image/') || IMAGE_FILE_EXTENSION_RE.test(fileName)
}

const isVideoFile = (file?: AnswerFileUrl | null) => {
    if (!file) return false

    const sourceType = (file.content_type || '').split(';')[0]?.trim().toLowerCase()
    const fileName = file?.name || ''

    return sourceType?.startsWith('video/') || VIDEO_FILE_EXTENSION_RE.test(fileName)
}

const getFilePreviewUrl = (file?: AnswerFileUrl | null) =>
    getPreferredPreviewUrl(file?.preview_urls, file?.preview_url)

export const getParentPayload = (answer?: Answer | null): ParentPayload | null => {
    if (!answer) return null

    const extractedUrls = extractUrls(answer?.delta_json)
    const attachmentState = answerModel.getComposeAttachmentState({
        answer,
        extractedUrls,
        hiddenPreviewUrls: [],
        resolveEmbedAnswer: embed => getCachedAnswer(String(embed?.id)) || embed,
    })
    const latestImageV2 = Array.isArray(answer.images_v2) ? answer.images_v2[0] : null
    const latestImageUrl = Array.isArray(answer.image_urls)
        ? answer.image_urls[answer.image_urls.length - 1]
        : null
    const imageUrl =
        getPreferredPreviewUrl(latestImageV2?.urls, latestImageV2?.preview_url) ||
        getPreferredPreviewUrl(latestImageUrl?.preview_urls, latestImageUrl?.preview_url) ||
        latestImageUrl?.image_url ||
        attachmentState.imageUrl ||
        answer.answer_image_url ||
        null
    const videoUrl = attachmentState.videoUrl
    const fileUrl = attachmentState.fileUrl
    const filePreviewUrl = getFilePreviewUrl(fileUrl)
    const attachedUrl = attachmentState.attachedUrl || answer.url || null
    const urlPreviewImage =
        attachedUrl?.preview_image_url || attachedUrl?.preview_url || attachedUrl?.image_urls?.[0]
    const isOrdinal =
        !!attachedUrl?.bsv_ordinal_id || isOrdinalUrl(attachedUrl?.address) || !!answer.has_ordinal

    if (imageUrl) {
        return { kind: 'image', previewUrl: imageUrl }
    }

    if (isImageFile(fileUrl) && filePreviewUrl) {
        return { kind: 'image', previewUrl: filePreviewUrl }
    }

    if (videoUrl || isVideoFile(fileUrl)) {
        return {
            kind: 'video',
            previewUrl: answer.recording_thumbnail_url || filePreviewUrl || null,
            icon: Play,
        }
    }

    if (isAudioFile(fileUrl)) {
        return { kind: 'audio', icon: Music4 }
    }

    if (isOrdinal) {
        return {
            kind: 'ordinal',
            previewUrl: urlPreviewImage || null,
            icon: Package,
        }
    }

    if (attachedUrl?.address) {
        return {
            kind: 'url',
            previewUrl: urlPreviewImage || null,
            icon: Link2,
        }
    }

    if (fileUrl) {
        return { kind: 'file', icon: File }
    }

    if (answer.embeds?.length || answer.files?.length) {
        return { kind: 'other', icon: ImageIcon }
    }

    return null
}
